import { CloseBold, InformationCircleBold } from '@ha/icons';
import React from 'react';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import cx from 'classnames';

import {
  Tooltip,
  SwipeableDrawer,
  IconButton,
  Divider,
  Grid,
  units,
} from '@ha/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { track } from 'ha/modules/Analytics/actions';

interface Props {
  text: React.ReactNode;
  title: React.ReactNode;
  analyticsName: string;
}

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  },
  tooltipCard: {
    padding: units.rem(1),
    margin: 0,
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.fontSize,
  },
  divider: {
    marginLeft: units.rem(-1),
    marginRight: units.rem(-1),
  },
  popper: {
    opacity: 1,
  },
  icon: {
    padding: 0,
    paddingLeft: '12px',
  },
}));

const SearchFiltersTooltip: React.FC<Props> = ({
  title,
  text,
  analyticsName,
}) => {
  const [open, setOpen] = React.useState(false);
  const { md } = useMediaQuery();
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleTooltipClose = React.useCallback(e => {
    e.stopPropagation();
    setOpen(false);
  }, []);
  const handleTooltipOpen = React.useCallback(
    e => {
      e.stopPropagation();

      setOpen(true);

      dispatch(
        track('Tooltip opened', {
          name: analyticsName,
          location: 'search',
        }),
      );
    },
    [dispatch, analyticsName],
  );

  return md ? (
    <Tooltip
      classes={{
        tooltip: cx(classes.tooltip, classes.tooltipCard),
        popper: classes.popper,
      }}
      PopperProps={{ disablePortal: true }}
      placement="right"
      open={open}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={<div>{text}</div>}
    >
      <IconButton
        onMouseEnter={handleTooltipOpen}
        onMouseLeave={handleTooltipClose}
        className={classes.icon}
      >
        <InformationCircleBold color="disabled" fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : (
    <React.Fragment>
      <IconButton onClick={handleTooltipOpen} className={classes.icon}>
        <InformationCircleBold color="disabled" fontSize="small" />
      </IconButton>
      <SwipeableDrawer
        anchor="bottom"
        PaperProps={{ className: cx(classes.drawerPaper, classes.tooltipCard) }}
        open={open}
        onClose={handleTooltipClose}
        onOpen={handleTooltipOpen}
      >
        <Grid container justify="space-between" alignItems="center">
          {title}
          <IconButton onClick={handleTooltipClose}>
            <CloseBold fontSize="small" />
          </IconButton>
        </Grid>
        <Divider kind="spaced-m" className={classes.divider} />
        {text}
      </SwipeableDrawer>
    </React.Fragment>
  );
};

export default SearchFiltersTooltip;
